import { DateTime } from "luxon";
import { isValidUuid } from "src/app/core/helpers/uuid.helper";
import {
  dobCalendarDayKey,
  formatDobMdY,
} from "src/app/core/utils/dob-display.util";
import {
  Appointment,
  WorksheetGridClientFilters,
  getAppointmentReferralId,
} from "src/app/core/models/admissions.model";
import { SelectableEnumerationItem } from "src/app/core/models/lookup.model";
import { WorksheetFilterSnapshot } from "./worksheet-saved-views.model";
import {
  formatWorksheetAuthorizationDisplay,
  formatWorksheetClaimStatusDisplay,
  formatWorksheetEligibilityDisplay,
  getWorksheetPrimaryPayerDisplay,
  worksheetEligibilityFilterTokenMatchesRow,
} from "src/app/core/helpers/worksheet-biller-columns.util";

/** Inclusive min/max `yyyy-MM-dd` from a comma-separated day-key CSV (date-range column filters). */
export function dayKeysCsvToInclusiveBounds(
  raw: string | null | undefined
): { start: string; end: string } | null {
  const parts = String(raw ?? "")
    .split(",")
    .map((x) => x.trim())
    .filter((x) => /^\d{4}-\d{2}-\d{2}$/.test(x))
    .sort();
  if (!parts.length) {
    return null;
  }
  return { start: parts[0], end: parts[parts.length - 1] };
}

function dayKeyInCsvRange(
  raw: string | null | undefined,
  dayKey: string | null | undefined
): boolean {
  const bounds = dayKeysCsvToInclusiveBounds(raw);
  if (!bounds) {
    return true;
  }
  const key = String(dayKey ?? "").trim();
  return !!key && key >= bounds.start && key <= bounds.end;
}

/** Sentinel value for the worksheet Source facet “--” row (no referral source / empty source name). */
export const WORKSHEET_SOURCE_BLANK_VALUE = "__worksheet_blank_source__";
/** Grid / column empty placeholder (unchanged in table cells). */
export const WORKSHEET_SOURCE_BLANK_LABEL = "--";
/** Blank facet row label in filter dropdowns / sidebars. */
export const WORKSHEET_FACET_BLANK_FILTER_LABEL = "Blank";
/** PHI directory `referral_source` query token for rows with no source. */
export const WORKSHEET_SOURCE_BLANK_DIRECTORY_QUERY = "no_source";

/** Sentinel for worklist DOB column blank filter (no date of birth). */
export const WORKSHEET_DOB_BLANK_VALUE = "__worksheet_blank_dob__";
/** PHI directory `dob` query token for rows with no DOB. */
export const WORKSHEET_DOB_BLANK_DIRECTORY_QUERY = "no_dob";
/** DOB filter sidebar checkbox label. */
export const WORKSHEET_DOB_BLANK_SIDEBAR_LABEL = "Blank";

export function isWorksheetDobBlankFilterValue(
  value: string | null | undefined
): boolean {
  const v = String(value ?? "").trim();
  if (!v) {
    return false;
  }
  return (
    v === WORKSHEET_DOB_BLANK_VALUE ||
    v.toLowerCase() === WORKSHEET_DOB_BLANK_DIRECTORY_QUERY
  );
}

export function worksheetDobBlankFilterAppliedTagLabel(): string {
  return "Blank DOB";
}

/** Sentinel for worklist Recent DOS column blank filter (no DOS). */
export const WORKSHEET_DOS_BLANK_VALUE = "__worksheet_blank_dos__";
/** PHI directory `last_dos` query token for rows with no DOS. */
export const WORKSHEET_DOS_BLANK_DIRECTORY_QUERY = "no_dos";

export function isWorksheetDosBlankFilterValue(
  value: string | null | undefined
): boolean {
  const v = String(value ?? "").trim();
  if (!v) {
    return false;
  }
  return (
    v === WORKSHEET_DOS_BLANK_VALUE ||
    v.toLowerCase() === WORKSHEET_DOS_BLANK_DIRECTORY_QUERY
  );
}

export function worksheetDosBlankFilterAppliedTagLabel(): string {
  return "Blank DOS";
}

export function isWorksheetReferralSourceBlankFilterValue(
  value: string | null | undefined
): boolean {
  return String(value ?? "").trim() === WORKSHEET_SOURCE_BLANK_VALUE;
}

export function worksheetReferralSourceBlankFilterRow(): SelectableEnumerationItem {
  return {
    displayName: WORKSHEET_FACET_BLANK_FILTER_LABEL,
    value: WORKSHEET_SOURCE_BLANK_VALUE,
    isSelected: false,
  };
}

/** Ensures the Source facet list includes the “--” option (first row). */
export function ensureWorksheetReferralSourceBlankFilterRow(
  rows: SelectableEnumerationItem[] | null | undefined
): SelectableEnumerationItem[] {
  const list = rows ?? [];
  const blankSelected = list.some(
    (r) => isWorksheetReferralSourceBlankFilterValue(r.value) && r.isSelected
  );
  const withoutBlank = list.filter((r) => !isWorksheetReferralSourceBlankFilterValue(r.value));
  return [
    { ...worksheetReferralSourceBlankFilterRow(), isSelected: blankSelected },
    ...withoutBlank,
  ];
}

export function pinWorksheetReferralSourceBlankFacetOptFirst(
  opts: { label: string; value: string }[]
): { label: string; value: string }[] {
  const blank = opts.find((o) => isWorksheetReferralSourceBlankFilterValue(o.value));
  if (!blank) {
    return opts;
  }
  return [blank, ...opts.filter((o) => !isWorksheetReferralSourceBlankFilterValue(o.value))];
}

/** Worklist / Engage facet columns that support a blank filter row → PHI directory `no_*` tokens. */
export type WorksheetBlankFacetKind =
  | "queue"
  | "patientStatus"
  | "encStatus"
  | "visitType"
  | "service"
  | "resource"
  | "location";

export const WORKSHEET_FACET_BLANK_DIRECTORY_QUERY: Record<
  WorksheetBlankFacetKind | "source",
  string
> = {
  source: WORKSHEET_SOURCE_BLANK_DIRECTORY_QUERY,
  queue: "no_queue",
  patientStatus: "no_patient_status",
  encStatus: "no_status",
  visitType: "no_visit_type",
  service: "no_service",
  resource: "no_resource",
  location: "no_location",
};

export function worksheetBlankFacetInternalValue(
  kind: WorksheetBlankFacetKind | "source"
): string {
  if (kind === "source") {
    return WORKSHEET_SOURCE_BLANK_VALUE;
  }
  return `__worksheet_blank_${kind}__`;
}

export function isWorksheetBlankFacetFilterValue(
  kind: WorksheetBlankFacetKind | "source",
  value: string | null | undefined
): boolean {
  const v = String(value ?? "").trim();
  if (!v) {
    return false;
  }
  if (kind === "source") {
    return isWorksheetReferralSourceBlankFilterValue(v);
  }
  if (v === worksheetBlankFacetInternalValue(kind)) {
    return true;
  }
  return v.toLowerCase() === WORKSHEET_FACET_BLANK_DIRECTORY_QUERY[kind];
}

export function worksheetBlankFacetDirectoryQueryValue(
  kind: WorksheetBlankFacetKind | "source",
  filterValue: string | null | undefined
): string {
  const v = String(filterValue ?? "").trim();
  if (!v) {
    return "";
  }
  if (isWorksheetBlankFacetFilterValue(kind, v)) {
    return WORKSHEET_FACET_BLANK_DIRECTORY_QUERY[kind];
  }
  return v;
}

export function worksheetBlankFacetOption(
  kind: WorksheetBlankFacetKind | "source"
): { label: string; value: string } {
  return {
    label: WORKSHEET_FACET_BLANK_FILTER_LABEL,
    value: worksheetBlankFacetInternalValue(kind),
  };
}

export function worksheetBlankFacetFilterRow(
  kind: WorksheetBlankFacetKind
): SelectableEnumerationItem {
  return {
    displayName: WORKSHEET_FACET_BLANK_FILTER_LABEL,
    value: worksheetBlankFacetInternalValue(kind),
    isSelected: false,
  };
}

/** Applied filter toolbar tag label when a blank facet is active. */
export function worksheetBlankFacetAppliedFilterTagLabel(
  kind: WorksheetBlankFacetKind | "source"
): string {
  switch (kind) {
    case "source":
      return "Blank Source";
    case "queue":
      return "Blank Stage";
    case "patientStatus":
      return "Patient Status";
    case "encStatus":
      return "Blank Encounter Status";
    case "visitType":
      return "Visit Type";
    case "service":
      return "Blank Service";
    case "resource":
      return "Resource";
    case "location":
      return "Location";
  }
}

export function ensureWorksheetBlankFacetFilterRow(
  kind: WorksheetBlankFacetKind | "source",
  rows: SelectableEnumerationItem[] | null | undefined
): SelectableEnumerationItem[] {
  if (kind === "source") {
    return ensureWorksheetReferralSourceBlankFilterRow(rows);
  }
  const list = rows ?? [];
  const blankSelected = list.some(
    (r) => isWorksheetBlankFacetFilterValue(kind, r.value) && r.isSelected
  );
  const withoutBlank = list.filter((r) => !isWorksheetBlankFacetFilterValue(kind, r.value));
  return [
    { ...worksheetBlankFacetFilterRow(kind), isSelected: blankSelected },
    ...withoutBlank,
  ];
}

export function pinWorksheetBlankFacetOptFirst(
  kind: WorksheetBlankFacetKind | "source",
  opts: { label: string; value: string }[]
): { label: string; value: string }[] {
  if (kind === "source") {
    return pinWorksheetReferralSourceBlankFacetOptFirst(opts);
  }
  const blank = opts.find((o) => isWorksheetBlankFacetFilterValue(kind, o.value));
  if (!blank) {
    return opts;
  }
  return [blank, ...opts.filter((o) => !isWorksheetBlankFacetFilterValue(kind, o.value))];
}

export function normalizeActiveBlankFacetFilterValues(
  kind: WorksheetBlankFacetKind | "source",
  active: string[]
): string[] {
  if (kind === "source") {
    return normalizeActiveReferralSourceFilterValues(active);
  }
  const tokens = (active ?? [])
    .map((v) => String(v ?? "").trim())
    .filter((v) => v.length > 0 && v.toLowerCase() !== "all");
  if (!tokens.length) {
    return [];
  }
  const hasBlank = tokens.some((w) => isWorksheetBlankFacetFilterValue(kind, w));
  const named = tokens.filter((w) => !isWorksheetBlankFacetFilterValue(kind, w));
  const out: string[] = [];
  if (hasBlank) {
    out.push(worksheetBlankFacetInternalValue(kind));
  }
  out.push(...named);
  return out;
}

export function worksheetColumnDisplayIsBlank(display: string | null | undefined): boolean {
  const d = (display ?? "").trim();
  return d === WORKSHEET_SOURCE_BLANK_LABEL || d === "—";
}

export function appointmentHasBlankReferralSource(appt: Appointment): boolean {
  const ext = appt as Appointment & {
    referral_source?: unknown;
    referral_source_id?: string;
    referralSourceName?: string;
    referral_source_name?: string;
    source_name?: string;
  };
  const flat = appt as unknown as Record<string, unknown>;
  const nameFields = [
    ext.referralSourceName,
    ext.referral_source_name,
    ext.source_name,
    flat["source_name"],
    flat["sourceName"],
    flat["referral_source_name"],
    flat["referralSourceName"],
  ];
  if (nameFields.some((v) => v != null && String(v).trim())) {
    return false;
  }
  const raw = ext.referralSource ?? ext.referral_source;
  if (raw != null && typeof raw === "object" && !Array.isArray(raw)) {
    const obj = raw as Record<string, unknown>;
    const nestedName = String(
      obj["source_name"] ??
        obj["sourceName"] ??
        obj["name"] ??
        obj["label"] ??
        obj["display"] ??
        ""
    ).trim();
    if (nestedName) {
      return false;
    }
    const nestedId = String(obj["source"] ?? obj["id"] ?? obj["referral_source_id"] ?? "").trim();
    if (nestedId) {
      return false;
    }
  }
  const scalarsAndIds = [
    typeof raw === "string" || typeof raw === "number" ? raw : null,
    ext.referralSourceId,
    ext.referral_source_id,
    flat["referral_source_id"],
    flat["referralSourceId"],
  ];
  return !scalarsAndIds.some((v) => v != null && String(v).trim());
}

/** Column helpers for client grid filters (same rules as `ListViewComponent`). */
/** Stable `n:` key from a display name (worklist name filter sidebar / directory `full_name`). */
export function worksheetNameFilterKeyFromDisplayName(displayName: string): string {
  const name = (displayName ?? "").trim() || "—";
  return `n:${name.toLowerCase().replace(/\s+/g, " ")}`;
}

/** Stable key for name-column multi-select (`id:` / `ref:` / `n:`). */
export function worksheetRowNameFilterKey(appt: Appointment): string {
  const pid = (appt.patientId ?? appt.encountersPatient?.id ?? "").toString().trim();
  if (pid) {
    return `id:${pid.toLowerCase()}`;
  }
  const rid = getAppointmentReferralId(appt).trim();
  if (rid) {
    return `ref:${rid.toLowerCase()}`;
  }
  const name = (appt.encountersPatient?.name ?? "").trim();
  return `n:${name.toLowerCase().replace(/\s+/g, " ")}`;
}

export interface WorksheetGridColumnMatchDeps {
  getWorksheetReferralQueueDisplay(a: Appointment): string;
  getWorksheetReferralSourceDisplay(a: Appointment): string;
  getWorksheetRefStatusDisplay(a: Appointment): string;
  getWorksheetCoordinatorDisplay(a: Appointment): string;
  getWorksheetCreatedDisplay(a: Appointment): string | null;
  getWorksheetUpdatedDisplay(a: Appointment): string | null;
  resolveServiceDisplayLabel?: (raw: string) => string;
  resolveServiceId?: (raw: string) => string;
}

/** True when the worklist Source column shows the empty placeholder (`--`). */
export function worksheetReferralSourceListDisplayIsBlank(
  display: string | null | undefined
): boolean {
  const d = (display ?? "").trim();
  return d === WORKSHEET_SOURCE_BLANK_LABEL || d === "—";
}

export function appointmentShowsBlankReferralSourceInList(
  appt: Appointment,
  getDisplay?: (a: Appointment) => string
): boolean {
  if (getDisplay) {
    return worksheetReferralSourceListDisplayIsBlank(getDisplay(appt));
  }
  return appointmentHasBlankReferralSource(appt);
}

/** Assignee user id on merged worklist rows when present as a UUID (PHI directory `assigned_to`). */
function worksheetRowCoordinatorAssigneeId(appt: Appointment): string {
  const a = appt as Appointment & { assignedTo?: string; assigned_to?: string };
  const v = (a.assignedTo ?? a.assigned_to ?? "").toString().trim();
  if (v && isValidUuid(v)) {
    return v.toLowerCase();
  }
  return "";
}

export function isoDateLocal(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function formatMMDDYYYY(d: Date): string {
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${mm}/${dd}/${d.getFullYear()}`;
}

export function activeNarrowingSelectableValues(
  rows: SelectableEnumerationItem[] | undefined
): string[] {
  return (rows ?? [])
    .filter(
      (x) =>
        x?.isSelected &&
        String(x.value ?? "").trim() &&
        String(x.value).toLowerCase() !== "all"
    )
    .map((x) => String(x.value).trim());
}

/** Location facet: match by display name and id (directory filter sends names). */
export function activeLocationFilterMatchTokens(
  rows: SelectableEnumerationItem[] | undefined
): string[] {
  const out: string[] = [];
  for (const x of rows ?? []) {
    if (
      !x?.isSelected ||
      !String(x.value ?? "").trim() ||
      String(x.value).toLowerCase() === "all"
    ) {
      continue;
    }
    const display = String(x.displayName ?? "").trim();
    const value = String(x.value ?? "").trim();
    if (display) {
      out.push(display);
    }
    if (value && value !== display) {
      out.push(value);
    }
  }
  return out;
}

export function normalizeReferralSourceFilterId(v: string): string {
  return v.trim().toLowerCase() === "meta" ? "online" : v.trim();
}

/** Blank source may be combined with named sources (OR). */
export function normalizeActiveReferralSourceFilterValues(active: string[]): string[] {
  const tokens = (active ?? [])
    .map((v) => String(v ?? "").trim())
    .filter((v) => v.length > 0 && v.toLowerCase() !== "all");
  if (!tokens.length) {
    return [];
  }
  const hasBlank = tokens.some((w) => isWorksheetReferralSourceBlankFilterValue(w));
  const named = tokens
    .filter((w) => !isWorksheetReferralSourceBlankFilterValue(w))
    .map(normalizeReferralSourceFilterId)
    .filter((v) => v.length > 0);
  const out: string[] = [];
  if (hasBlank) {
    out.push(WORKSHEET_SOURCE_BLANK_VALUE);
  }
  out.push(...named);
  return out;
}

export function rowMatchesReferralSourceFilter(
  appt: Appointment,
  active: string[],
  deps?: Pick<WorksheetGridColumnMatchDeps, "getWorksheetReferralSourceDisplay">
): boolean {
  const normalized = normalizeActiveReferralSourceFilterValues(active);
  if (!normalized.length) {
    return true;
  }
  const wantsBlank = normalized.some((v) =>
    isWorksheetReferralSourceBlankFilterValue(v)
  );
  const nonBlankActive = normalized.filter(
    (v) => !isWorksheetReferralSourceBlankFilterValue(v)
  );
  if (
    appointmentShowsBlankReferralSourceInList(appt, deps?.getWorksheetReferralSourceDisplay)
  ) {
    if (wantsBlank) {
      return true;
    }
    if (!nonBlankActive.length) {
      return false;
    }
  } else if (!nonBlankActive.length) {
    return false;
  }
  const ext = appt as Appointment & {
    referral_source?: string;
    referral_source_id?: string;
  };
  const candidates: string[] = [];
  const push = (v: unknown): void => {
    if (v == null || v === "") {
      return;
    }
    const s = String(v).trim();
    if (s) {
      candidates.push(s);
    }
  };
  push(ext.referralSource);
  push(ext.referral_source);
  push(ext.referralSourceId);
  push(ext.referral_source_id);
  if (!candidates.length) {
    return false;
  }
  return nonBlankActive.some((want) => {
    const wNorm = normalizeReferralSourceFilterId(want).trim();
    const wl = wNorm.toLowerCase();
    return candidates.some((raw) => {
      const rNorm = normalizeReferralSourceFilterId(raw).trim();
      const rl = rNorm.toLowerCase();
      return (
        rNorm === wNorm ||
        rl === wl ||
        raw.trim() === want.trim() ||
        raw.trim().toLowerCase() === want.trim().toLowerCase()
      );
    });
  });
}

export function rowResourceKey(appt: Appointment): string | null {
  const flat = appt as unknown as Record<string, unknown>;
  const id =
    appt.scheduleResourceId ||
    appt.resourceId ||
    appt.encounterSchedule?.scheduleResourceId ||
    flat["schedule_resource"] ||
    flat["schedule_resource_id"];
  if (id != null && String(id).trim()) {
    return String(id).trim().toLowerCase();
  }
  const name = appt.resourceName?.trim();
  return name ? name.toLowerCase() : null;
}

export function rowVisitTypeKey(appt: Appointment): string | null {
  const vt = appt.encounterSchedule?.visitType ?? appt.visitType;
  if (vt && String(vt).trim()) {
    return String(vt).trim().toLowerCase();
  }
  return null;
}

export function rowMatchesLocationFilter(
  appt: Appointment,
  active: string[]
): boolean {
  const normalized = normalizeActiveBlankFacetFilterValues("location", active);
  if (!normalized.length) {
    return true;
  }
  const wantsBlank = normalized.some((v) =>
    isWorksheetBlankFacetFilterValue("location", v)
  );
  const named = normalized.filter(
    (v) => !isWorksheetBlankFacetFilterValue("location", v)
  );
  const room = (appt.encounterSchedule?.procedureRoom ?? "").toString().trim();
  const locId = (appt.locationId ?? "").toString().trim();
  if (!room && !locId) {
    return wantsBlank;
  }
  if (!named.length) {
    return false;
  }
  const locIdL = locId.toLowerCase();
  const roomL = room.toLowerCase();
  return named.some((v) => {
    const s = v.trim().toLowerCase();
    if (!s) {
      return false;
    }
    return (
      (locIdL && locIdL === s) ||
      (roomL && roomL === s) ||
      (locIdL && s === locIdL) ||
      (roomL && s.includes(roomL)) ||
      (roomL && roomL.includes(s))
    );
  });
}

export function rowMatchesResourceFilter(
  appt: Appointment,
  resourcesFilter: SelectableEnumerationItem[] | undefined | null
): boolean {
  const selected = (resourcesFilter ?? []).filter(
    (x) =>
      x?.isSelected &&
      String(x.value ?? "").trim() &&
      String(x.value).toLowerCase() !== "all"
  );
  if (!selected.length) {
    return true;
  }
  const wantsBlank = selected.some((row) =>
    isWorksheetBlankFacetFilterValue("resource", row.value)
  );
  const namedRows = selected.filter(
    (row) => !isWorksheetBlankFacetFilterValue("resource", row.value)
  );
  const flat = appt as unknown as Record<string, unknown>;
  const idCandidates = [
    appt.scheduleResourceId,
    appt.resourceId,
    appt.encounterSchedule?.scheduleResourceId,
    flat["schedule_resource"],
    flat["schedule_resource_id"],
    flat["resources"],
  ]
    .map((x) => (x != null ? String(x).trim().toLowerCase() : ""))
    .filter(Boolean);
  const nameKey = (appt.resourceName ?? appt.providerName ?? "").trim().toLowerCase();
  if (idCandidates.length === 0 && !nameKey) {
    return wantsBlank;
  }
  if (!namedRows.length) {
    return false;
  }

  return namedRows.some((row) => {
    const idWant = String(row.value ?? "").trim().toLowerCase();
    const nameWant = String(row.displayName ?? "").trim().toLowerCase();
    if (!idWant) {
      return false;
    }
    if (idCandidates.some((id) => id === idWant || id.includes(idWant) || idWant.includes(id))) {
      return true;
    }
    if (
      nameWant &&
      nameKey &&
      (nameKey === nameWant || nameKey.includes(nameWant) || nameWant.includes(nameKey))
    ) {
      return true;
    }
    if (
      nameKey &&
      (nameKey === idWant || nameKey.includes(idWant) || idWant.includes(nameKey))
    ) {
      return true;
    }
    return false;
  });
}

export function rowMatchesVisitTypeFilter(
  appt: Appointment,
  active: string[]
): boolean {
  const normalized = normalizeActiveBlankFacetFilterValues("visitType", active);
  if (!normalized.length) {
    return true;
  }
  const wantsBlank = normalized.some((v) =>
    isWorksheetBlankFacetFilterValue("visitType", v)
  );
  const named = normalized.filter(
    (v) => !isWorksheetBlankFacetFilterValue("visitType", v)
  );
  const vk = rowVisitTypeKey(appt);
  if (!vk) {
    return wantsBlank;
  }
  if (!named.length) {
    return false;
  }
  return named.some((v) => {
    const s = v.trim().toLowerCase();
    return s && (vk === s || vk.includes(s) || s.includes(vk));
  });
}

export function rowMatchesServiceFilter(
  appt: Appointment,
  active: string[],
  resolveServiceLabel?: (raw: string) => string,
  resolveServiceId?: (raw: string) => string
): boolean {
  const normalized = normalizeActiveBlankFacetFilterValues("service", active);
  if (!normalized.length) {
    return true;
  }
  const wantsBlank = normalized.some((v) =>
    isWorksheetBlankFacetFilterValue("service", v)
  );
  const named = normalized.filter(
    (v) => !isWorksheetBlankFacetFilterValue("service", v)
  );
  const raw = (appt.service ?? "").trim();
  if (!raw) {
    return wantsBlank;
  }
  if (!named.length) {
    return false;
  }
  const apptId = (resolveServiceId?.(raw) ?? raw).trim().toLowerCase();
  const apptLabel = (resolveServiceLabel?.(raw) ?? raw).trim().toLowerCase();
  return named.some((v) => {
    const id = v.trim();
    if (!id) {
      return false;
    }
    const key = id.toLowerCase();
    if (key === apptId || key === raw.toLowerCase()) {
      return true;
    }
    const filterLabel = (resolveServiceLabel?.(id) ?? "").trim().toLowerCase();
    return !!filterLabel && filterLabel === apptLabel;
  });
}

export function rowMatchesEncounterStatusFilter(
  appt: Appointment,
  active: string[]
): boolean {
  const normalized = normalizeActiveBlankFacetFilterValues("encStatus", active);
  if (!normalized.length) {
    return true;
  }
  const wantsBlank = normalized.some((v) =>
    isWorksheetBlankFacetFilterValue("encStatus", v)
  );
  const named = normalized.filter(
    (v) => !isWorksheetBlankFacetFilterValue("encStatus", v)
  );
  const les = (appt.lastEncounterStatus ?? "").toString().trim();
  if (!les) {
    return wantsBlank;
  }
  if (!named.length) {
    return false;
  }
  const lesL = les.toLowerCase();
  return named.some((v) => {
    const t = v.trim().toLowerCase();
    if (!t) {
      return false;
    }
    return (
      lesL === t ||
      (lesL && lesL.includes(t)) ||
      (t && lesL && t.includes(lesL))
    );
  });
}

/** True when the row’s DOS (`encounterDateTime`) falls within the filter calendar range (local days). */
export function rowMatchesSnapshotDosRange(
  appt: Appointment,
  filterStartRaw: string | null | undefined,
  filterEndRaw?: string | null | undefined
): boolean {
  const raw = (filterStartRaw ?? "").trim();
  if (!raw) {
    return true;
  }
  const startDt = DateTime.fromISO(raw, { zone: "local" });
  if (!startDt.isValid) {
    return true;
  }
  const endTrim = (filterEndRaw ?? "").trim();
  const endParsed = endTrim ? DateTime.fromISO(endTrim, { zone: "local" }) : startDt;
  const endDay = endParsed.isValid ? endParsed : startDt;
  const d1 = startDt.startOf("day");
  const d2 = endDay.startOf("day");
  const lo = d1 <= d2 ? d1 : d2;
  const hi = d1 <= d2 ? d2.endOf("day") : d1.endOf("day");
  const enc = appt.encounterDateTime;
  if (enc == null || !String(enc).trim()) {
    return false;
  }
  const ed = DateTime.fromISO(String(enc), { zone: "local" });
  return ed.isValid && ed >= lo && ed <= hi;
}

/** Local calendar day key (`yyyy-MM-dd`) from `encounterDateTime`, or null if missing/invalid. */
export function appointmentDosLocalDayKey(enc: string | null | undefined): string | null {
  if (enc == null || !String(enc).trim()) {
    return null;
  }
  const ed = DateTime.fromISO(String(enc), { zone: "local" });
  return ed.isValid ? ed.toFormat("yyyy-MM-dd") : null;
}

/** @deprecated Prefer {@link rowMatchesSnapshotDosRange} (end optional = single day). */
export function rowMatchesSnapshotDosDay(
  appt: Appointment,
  filterDateRaw: string | null | undefined
): boolean {
  return rowMatchesSnapshotDosRange(appt, filterDateRaw, undefined);
}

/**
 * Client-side row match for saved-view counts: same facets + grid text as `applyWorksheetSnapshot` + list load.
 * Name search is applied server-side; we still match `nameContains` here when the broad list includes non-matching names.
 */
export function matchesWorksheetGridClientFiltersState(
  appt: Appointment,
  gridText: WorksheetGridClientFilters,
  createdOnIso: string | null | undefined,
  updatedOnIso: string | null | undefined,
  deps: WorksheetGridColumnMatchDeps,
  createdOnEndIso?: string | null | undefined
): boolean {
  const f = gridText;
  if ((f.nameContains ?? "").trim()) {
    const sub = f.nameContains.trim().toLowerCase();
    const name = (appt.encountersPatient?.name ?? "").toLowerCase();
    if (!name.includes(sub)) {
      return false;
    }
  }
  if ((f.namePatientKeysContains ?? "").trim()) {
    const parts = f.namePatientKeysContains!
      .split(",")
      .map((x) => x.trim())
      .filter(Boolean);
    const set = new Set(parts);
    const rowKey = worksheetRowNameFilterKey(appt);
    const rowNameKey = worksheetNameFilterKeyFromDisplayName(
      (appt.encountersPatient?.name ?? "").trim() || "—"
    );
    if (!set.has(rowKey) && !set.has(rowNameKey)) {
      return false;
    }
  }
  if (isWorksheetDobBlankFilterValue(f.dobContains)) {
    if (appt.encountersPatient?.dob) {
      return false;
    }
  } else if ((f.dobContains ?? "").trim()) {
    const sub = f.dobContains.trim().toLowerCase();
    const dobRaw = appt.encountersPatient?.dob;
    if (!dobRaw) {
      return false;
    }
    const dobStr =
      formatDobMdY(String(dobRaw), true).toLowerCase() ||
      String(dobRaw).toLowerCase();
    if (!dobStr.includes(sub)) {
      return false;
    }
  }
  if ((f.dobDayKeysContains ?? "").trim()) {
    const dobRaw = appt.encountersPatient?.dob;
    const key = dobRaw ? dobCalendarDayKey(dobRaw) : null;
    if (!dayKeyInCsvRange(f.dobDayKeysContains, key)) {
      return false;
    }
  }
  if ((f.queueContains ?? "").trim()) {
    const parts = f.queueContains
      .split(",")
      .map((x) => x.trim())
      .filter(Boolean);
    const normalized = normalizeActiveBlankFacetFilterValues("queue", parts);
    const wantsBlank = normalized.some((v) =>
      isWorksheetBlankFacetFilterValue("queue", v)
    );
    const named = normalized.filter(
      (v) => !isWorksheetBlankFacetFilterValue("queue", v)
    );
    const a = appt as Appointment & { referralQueueId?: string };
    const qid = (a.referralQueueId ?? "").toString().trim();
    const q = deps.getWorksheetReferralQueueDisplay(appt).trim();
    const isBlankRow =
      !qid && (!q || q === "--" || q.toLowerCase() === "set queue");
    if (isBlankRow) {
      if (!wantsBlank) {
        return false;
      }
    } else if (!named.length) {
      return false;
    } else {
      const tokens = named.map((v) => v.trim().toLowerCase()).filter(Boolean);
      const qidL = qid.toLowerCase();
      const matchesByQueId = !!(qidL && tokens.some((p) => qidL === p));
      const matchesDisplay = tokens.some((p) => p.length > 0 && q.toLowerCase().includes(p));
      if (!matchesByQueId && !matchesDisplay) {
        return false;
      }
    }
  }
  if ((f.refStatusContains ?? "").trim()) {
    const parts = f.refStatusContains
      .split(",")
      .map((x) => x.trim())
      .filter(Boolean);
    const normalized = normalizeActiveBlankFacetFilterValues("patientStatus", parts);
    const wantsBlank = normalized.some((v) =>
      isWorksheetBlankFacetFilterValue("patientStatus", v)
    );
    const named = normalized.filter(
      (v) => !isWorksheetBlankFacetFilterValue("patientStatus", v)
    );
    const disp = deps.getWorksheetRefStatusDisplay(appt).trim();
    if (worksheetColumnDisplayIsBlank(disp)) {
      if (!wantsBlank) {
        return false;
      }
    } else if (!named.length) {
      return false;
    } else {
      const dispL = disp.toLowerCase();
      if (
        !named.some((p) => {
          return dispL === p.trim().toLowerCase() || dispL.includes(p.trim().toLowerCase());
        })
      ) {
        return false;
      }
    }
  }
  if ((f.coordinatorContains ?? "").trim()) {
    const raw = f.coordinatorContains.trim();
    const tokens = raw.includes(",")
      ? raw.split(",").map((x) => x.trim()).filter(Boolean)
      : [raw];
    if (tokens.length) {
      const coord = deps.getWorksheetCoordinatorDisplay(appt).toLowerCase();
      const assigneeId = worksheetRowCoordinatorAssigneeId(appt);
      if (
        !tokens.some((t) => {
          const tl = t.toLowerCase();
          if (tl === "everyone") {
            return !assigneeId;
          }
          if (isValidUuid(t)) {
            return assigneeId === tl;
          }
          return tl.length > 0 && coord.includes(tl);
        })
      ) {
        return false;
      }
    }
  }
  if ((f.visitTypeContains ?? "").trim()) {
    const raw = f.visitTypeContains.trim().toLowerCase();
    const vt = rowVisitTypeKey(appt) ?? "";
    const tokens = raw.includes(",")
      ? raw
          .split(",")
          .map((x) => x.trim())
          .filter(Boolean)
      : [raw];
    if (!tokens.some((sub) => sub && (vt === sub || vt.includes(sub) || sub.includes(vt)))) {
      return false;
    }
  }
  if (isWorksheetDosBlankFilterValue(f.dosContains)) {
    const lastDos = String((appt as Appointment & { lastDosDateTime?: string }).lastDosDateTime ?? "").trim();
    const key =
      appointmentDosLocalDayKey(lastDos) ||
      appointmentDosLocalDayKey(appt.encounterDateTime);
    if (key) {
      return false;
    }
  } else if ((f.dosDayKeysContains ?? "").trim()) {
    const lastDos = String((appt as Appointment & { lastDosDateTime?: string }).lastDosDateTime ?? "").trim();
    const key =
      appointmentDosLocalDayKey(lastDos) ||
      appointmentDosLocalDayKey(appt.encounterDateTime);
    if (!dayKeyInCsvRange(f.dosDayKeysContains, key)) {
      return false;
    }
  }
  if ((f.createdDayKeysContains ?? "").trim()) {
    const raw = deps.getWorksheetCreatedDisplay(appt);
    const key = raw ? appointmentDosLocalDayKey(raw) : null;
    if (!dayKeyInCsvRange(f.createdDayKeysContains, key)) {
      return false;
    }
  }
  if ((f.updatedDayKeysContains ?? "").trim()) {
    const raw = deps.getWorksheetUpdatedDisplay(appt);
    const key = raw ? appointmentDosLocalDayKey(raw) : null;
    if (!dayKeyInCsvRange(f.updatedDayKeysContains, key)) {
      return false;
    }
  }
  if (createdOnIso) {
    const raw = deps.getWorksheetCreatedDisplay(appt);
    if (!raw) {
      return false;
    }
    const cd = new Date(raw);
    if (isNaN(cd.getTime())) {
      return false;
    }
    const dayKey = isoDateLocal(cd);
    const start = String(createdOnIso).trim().slice(0, 10);
    const endRaw = (createdOnEndIso ?? createdOnIso).toString().trim().slice(0, 10);
    const end = /^\d{4}-\d{2}-\d{2}$/.test(endRaw) ? endRaw : start;
    const lo = start <= end ? start : end;
    const hi = start <= end ? end : start;
    if (dayKey < lo || dayKey > hi) {
      return false;
    }
  }
  if (updatedOnIso) {
    const raw = deps.getWorksheetUpdatedDisplay(appt);
    if (!raw) {
      return false;
    }
    const ud = new Date(raw);
    if (isNaN(ud.getTime()) || isoDateLocal(ud) !== updatedOnIso) {
      return false;
    }
  }
  if ((f.payerContains ?? "").trim()) {
    const tokens = f.payerContains!
      .split(",")
      .map((x) => x.trim())
      .filter(Boolean);
    const payer = getWorksheetPrimaryPayerDisplay(appt);
    if (
      !tokens.some(
        (t) => payer.trim().toLowerCase() === t.trim().toLowerCase()
      )
    ) {
      return false;
    }
  }
  if ((f.eligibilityContains ?? "").trim()) {
    const tokens = f.eligibilityContains!
      .split(",")
      .map((x) => x.trim())
      .filter(Boolean);
    const elig = formatWorksheetEligibilityDisplay(appt.eligibility);
    if (
      !tokens.some(
        (t) =>
          worksheetEligibilityFilterTokenMatchesRow(t, appt.eligibility) ||
          elig.trim().toLowerCase() === t.trim().toLowerCase()
      )
    ) {
      return false;
    }
  }
  if ((f.authStatusContains ?? "").trim()) {
    const tokens = f.authStatusContains!
      .split(",")
      .map((x) => x.trim())
      .filter(Boolean);
    const auth = formatWorksheetAuthorizationDisplay(appt.authorizationStatus);
    if (
      !tokens.some(
        (t) => auth.trim().toLowerCase() === t.trim().toLowerCase()
      )
    ) {
      return false;
    }
  }
  if ((f.claimStatusContains ?? "").trim()) {
    const tokens = f.claimStatusContains!
      .split(",")
      .map((x) => x.trim())
      .filter(Boolean);
    const claimRaw = (appt.claimStatus ?? "").trim() || "--";
    const claimDisp = formatWorksheetClaimStatusDisplay(appt.claimStatus);
    if (
      !tokens.some((t) => {
        const tl = t.trim().toLowerCase();
        return (
          claimRaw.toLowerCase() === tl ||
          claimDisp.trim().toLowerCase() === tl
        );
      })
    ) {
      return false;
    }
  }
  return true;
}

export function appointmentMatchesWorksheetFilterSnapshot(
  appt: Appointment,
  snapshot: WorksheetFilterSnapshot,
  deps: WorksheetGridColumnMatchDeps
): boolean {
  if (
    !matchesWorksheetGridClientFiltersState(
      appt,
      snapshot.gridText,
      snapshot.createdOnIso,
      snapshot.updatedOnIso,
      deps,
      snapshot.createdOnEndIso
    )
  ) {
    return false;
  }
  const rs = normalizeActiveReferralSourceFilterValues(
    activeNarrowingSelectableValues(snapshot.referralSourceFilterList).map(
      normalizeReferralSourceFilterId
    )
  );
  if (!rowMatchesReferralSourceFilter(appt, rs, deps)) {
    return false;
  }
  if (!rowMatchesLocationFilter(appt, activeLocationFilterMatchTokens(snapshot.locationFilter))) {
    return false;
  }
  if (!rowMatchesResourceFilter(appt, snapshot.resourcesFilter)) {
    return false;
  }
  if (!rowMatchesVisitTypeFilter(appt, activeNarrowingSelectableValues(snapshot.visitTypeFilter))) {
    return false;
  }
  if (
    !rowMatchesServiceFilter(
      appt,
      activeNarrowingSelectableValues(snapshot.serviceFilter),
      deps.resolveServiceDisplayLabel,
      deps.resolveServiceId
    )
  ) {
    return false;
  }
  if (
    !rowMatchesEncounterStatusFilter(
      appt,
      activeNarrowingSelectableValues(snapshot.encounerStatusList)
    )
  ) {
    return false;
  }
  if (!rowMatchesSnapshotDosRange(appt, snapshot.filterDate, snapshot.filterEndDate)) {
    return false;
  }
  return true;
}
